import { BaseResponse, GenericResponse, TestLinkResponse } from "@/common/common_utils";
import { BulkProcessRequest, UploadEmployeesResponse } from "@/components/form-inputs/FileUpload4Form";
import { ENDPOINTS } from "@/constants/endpoints";
import { X4bUserRoleEnum } from "@/constants/roles";
import { VALIDATION_MESSAGES } from "@/constants/validation";
import FlagsModel, { Flag } from "@/modules/authentication/sign-up/components/CountryFlags";
import { showError, showNotification } from "@/modules/notification/engine/notification.library";
import { axiosInstance } from "@/services/axios.service";
import { useQuery } from "@tanstack/react-query";
import { mixed, number, object, string } from "yup";
import { EmployeeDto } from "./people.types";
import { v4 as uuidv4 } from 'uuid';
import { PhoneHelper } from "@/common/PhoneHelper";
import { isValidPhoneNumber } from "libphonenumber-js";
import { link } from "node:fs/promises";

/* Domain-Driven Organization: Feature-Based Architecture

This module groups code by business domain ("People") rather than by technical concerns.
All People-related functionality lives together: API calls, validation, types, transformations.

TRADITIONAL APPROACH (Layered/Horizontal Slicing):
├── api/
│   ├── peopleApi.ts
│   └── accountApi.ts
├── validation/
│   ├── peopleValidation.ts
│   └── accountValidation.ts
└── types/
		├── peopleTypes.ts
		└── accountTypes.ts

THIS APPROACH (Feature-Based/Vertical Slicing):
├── people/
│   └── People.ts (API, validation, types together)
└── account/
		└── Account.ts (API, validation, types together)

WHY THIS APPROACH?
- High cohesion: Related code stays together
- Clear ownership: Teams can own entire features
- Easier refactoring: Changes to People touch one module
- Reduced cognitive load: No hunting across technical layers
- Better scalability: Adding features doesn't fragment across layer directories

ARCHITECTURAL NOTE:
Traditional Layered Architecture (technical partitioning) often stems from misreading
architectural patterns. Clean Architecture and Onion Architecture prescribe RUNTIME
layering (dependency direction, boundaries), not compile-time folder structures.
Many developers bluntly project conceptual layers onto physical folders.

Uncle Bob's "Screaming Architecture": folders should reveal business domains, not frameworks.

THE PRINCIPLE:
Prioritize domain cohesion over technical layering in folder structure. Duplicate elimination
and feature maintainability have higher priority than rigid technical separation.

Note, initially I nambed the namespace PeopleEngine. See cursor_engine_and_feature_based.md for detailed discussion.
*/
export namespace People {

	export const sendInvites = async (fileUrl: string, operationType: string, groupId?: number) => {
		var res = await axiosInstance.post<GenericResponse<UploadEmployeesResponse>>(
			ENDPOINTS['/api/X4bEmployee/BulkProcess'], {
				FileUrl: fileUrl,
				OperationType: 'add-people',
				GroupId: groupId
			} as BulkProcessRequest);
		const failed = res.data.Data.FailedRiders;
		if (failed.length > 0) {
			showError(`Some riders are failed to import.\n ${failed.map(i => `${i.Phone}: ${i.Reason}`).join("\n")}`);
		} else {
			showNotification('Employees added successfully');
		}

		res.data.Data.InviteLinks.forEach(linkObj => {
			console.log(`Employee id: ${linkObj.EmployeeId} invite link: ${linkObj.Link}`);
		});
	}

	export interface EmployeeForm {
		FirstName: string;
		LastName: string;
		Email: string;
		Phone: string;
		ReferenceCode?: string | null;
		Country?: Flag;
		RoleId: number;
	}
	export const employeeFormSchema = object({
		FirstName: string().trim().required(VALIDATION_MESSAGES.required)
			.test('no-numbers', 'Names cannot contain numbers', function (value) {
				if (!value) return true; // Let required validation handle empty values
				return !/\d/.test(value);
			}),
		LastName: string().trim().required(VALIDATION_MESSAGES.required)
			.test('no-numbers', 'Names cannot contain numbers', function (value) {
				if (!value) return true; // Let required validation handle empty values
				return !/\d/.test(value);
			}),
		Email: string().trim().required(VALIDATION_MESSAGES.required).email('Invalid email format'),
		// Andrew told to make it required. Because it is required for the xpress proxy.
		Phone: string().trim().required(VALIDATION_MESSAGES.required)
			.test('is-valid-phone', 'Please enter a valid phone number', function (value) {
				if (!value) return true; // Let required validation handle empty values
				try {
					const countryCode = this.parent.mobileNumberPrefix || 'PH';
					const prepared = PhoneHelper.preparePhoneNumber(value, countryCode);
					const res = isValidPhoneNumber(prepared, countryCode);
					return res;
				} catch (error) {
					// If validation fails, return false
					return false;
				}
			}),
		ReferenceCode: string().nullable().optional(),
		Country: mixed<Flag>().optional(),
		RoleId: number()
			.typeError(VALIDATION_MESSAGES.required)
			.required(VALIDATION_MESSAGES.required)
			.oneOf([X4bUserRoleEnum.Admin, X4bUserRoleEnum.Manager, X4bUserRoleEnum.User], 'Invalid role'),
	});

	export const getEmployeeForm = async (employeeId: number): Promise<People.EmployeeForm> => {
		const response = await axiosInstance.post<GenericResponse<EmployeeDto>>(ENDPOINTS['/api/X4bEmployee/GetEmployee'], { Id: employeeId });
		const employee = response.data.Data;
		return {
			...employee,
			ReferenceCode: employee.ReferenceCode || '',
			// Cast: legacy employees may have no role; the form will require one before save.
			RoleId: employee.RoleIds?.[0] as number,
			Country: FlagsModel.flagByPhone(employee.Phone),
			Phone: FlagsModel.removeDialCode(employee.Phone)
		};
	}

	export const sendEmployeeForm = async (data: EmployeeForm, isAdd: boolean) => {
		let toSend: EmployeeDto = {
			...data,
			RoleIds: [data.RoleId],
			Phone: PhoneHelper.preparePhoneNumber(data.Phone, data.Country?.code || 'PH')
		}
		if (isAdd) {
			toSend.XpressId = uuidv4();

			const response = await axiosInstance.post<GenericResponse<TestLinkResponse>>(ENDPOINTS['/api/X4bEmployee/CreateEmployee'], toSend);
			// This log should remain at least until we have proper emailing.
			console.log(`Employee invite link: ${response.data.Data.Link}`);
		}
		else
			await axiosInstance.post<BaseResponse>(ENDPOINTS['/api/X4bEmployee/UpdateEmployee'], toSend);
	}

}

